import { useState } from 'react';
import type { TimeFormat } from '@timato/shared';
import { cn } from '@/lib/cn';

type Step = 'hour' | 'minute';
const SIZE = 232;
const R_OUTER = 96;
const R_INNER = 62;

const pad2 = (n: number) => String(n).padStart(2, '0');
const split = (hm: string): [number, number] => { const m = /^(\d{2}):(\d{2})$/.exec(hm); return m ? [Number(m[1]), Number(m[2])] : [9, 0]; };

/**
 * The phone's clock: pick the hour on a dial, then the minute, then you are done. Every number is a real button, so
 * it tabs and reads like a list. 24-hour mode has two rings (Material's layout); 12-hour mode has one and an am/pm
 * switch. Minutes are in fives; the text field beside it takes anything finer.
 */
export function ClockDial({ value, timeFormat, onChange, onDone }: { value: string; timeFormat: TimeFormat; onChange: (hm: string) => void; onDone?: () => void }) {
  const [h, m] = split(value);
  const [step, setStep] = useState<Step>('hour');
  const pm = h >= 12;
  const set = (hour: number, minute: number) => onChange(`${pad2(hour)}:${pad2(minute)}`);

  const hourLabel = timeFormat === '12h' ? String(((h + 11) % 12) + 1) : pad2(h);
  const hours = timeFormat === '12h'
    ? Array.from({ length: 12 }, (_, i) => ({ n: i + 1, value: ((i + 1) % 12) + (pm ? 12 : 0), ring: R_OUTER }))
    : [...Array.from({ length: 12 }, (_, i) => ({ n: i, value: i, ring: i === 0 ? R_INNER : R_OUTER })), ...Array.from({ length: 12 }, (_, i) => ({ n: i + 12, value: i + 12, ring: i === 0 ? R_OUTER : R_INNER }))];
  const minutes = Array.from({ length: 12 }, (_, i) => ({ n: i * 5, value: i * 5, ring: R_OUTER }));
  const marks = step === 'hour' ? hours : minutes;
  const selected = step === 'hour' ? h : m;
  const selectedMark = marks.find((k) => k.value === selected) ?? (step === 'minute' ? { n: m, value: m, ring: R_OUTER } : marks[0]!);
  const per = step === 'hour' ? 12 : 60;
  const angle = (n: number) => (n % per) * (360 / per);
  const at = (n: number, ring: number) => ({ transform: `translate(-50%, -50%) rotate(${angle(n)}deg) translateY(${-ring}px) rotate(${-angle(n)}deg)` });
  const part = (s: Step, label: string) => (
    <button type="button" onClick={() => setStep(s)} aria-pressed={step === s} className={cn('px-2 py-1 rounded-[8px] border-0 cursor-pointer font-mono text-[28px] font-semibold tabular-nums', step === s ? 'bg-inset text-ink' : 'bg-transparent text-ink-2 hover:text-ink')}>{label}</button>
  );

  return (
    <div className="grid gap-3 justify-items-center select-none">
      <div className="flex items-center gap-1">
        {part('hour', hourLabel)}<span className="text-ink-2 text-[28px] font-mono">:</span>{part('minute', pad2(m))}
        {timeFormat === '12h' && (
          <div className="grid gap-[2px] ml-3 text-[11px] font-extrabold" role="group" aria-label="am or pm">
            {(['am', 'pm'] as const).map((half) => <button key={half} type="button" aria-pressed={pm === (half === 'pm')} onClick={() => set((h % 12) + (half === 'pm' ? 12 : 0), m)} className={cn('px-2 py-[3px] rounded-[6px] border-0 cursor-pointer uppercase', pm === (half === 'pm') ? 'bg-accent text-on-project' : 'bg-inset text-ink-2 hover:text-ink')}>{half}</button>)}
          </div>
        )}
      </div>
      <div className="relative rounded-full bg-inset" style={{ width: SIZE, height: SIZE }} role="group" aria-label={step === 'hour' ? 'Hour' : 'Minute'}>
        {/* The hand, from the centre to the selected mark. */}
        <span aria-hidden="true" className="absolute left-1/2 top-1/2 w-[2px] bg-accent" style={{ height: selectedMark.ring, transform: `translateX(-50%) translateY(-100%) rotate(${angle(selectedMark.n)}deg)`, transformOrigin: '50% 100%' }} />
        <span aria-hidden="true" className="absolute left-1/2 top-1/2 size-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent" />
        {marks.map((k) => {
          const on = k.value === selected;
          return (
            <button key={`${step}-${k.n}`} type="button" aria-label={step === 'hour' ? `${k.n} o'clock` : `${pad2(k.n)} minutes`} aria-pressed={on}
              onClick={() => { if (step === 'hour') { set(k.value, m); setStep('minute'); } else { set(h, k.value); onDone?.(); } }}
              className={cn('absolute left-1/2 top-1/2 grid place-items-center rounded-full border-0 cursor-pointer font-mono tabular-nums', k.ring === R_INNER ? 'size-8 text-[12px]' : 'size-9 text-[14px]', on ? 'bg-accent text-on-project font-bold' : 'bg-transparent text-ink hover:bg-card')}
              style={at(k.n, k.ring)}>
              {step === 'minute' || timeFormat === '24h' ? pad2(k.n) : k.n}
            </button>
          );
        })}
      </div>
    </div>
  );
}
